import { useMemo } from 'react'
import axios from 'axios'
import useSWR, { KeyedMutator } from 'swr'
import useSWRInfinite from 'swr/infinite'
import { useLocale } from 'hooks'
import { APIResponseUser, User } from '../types/user'
import { BillingAddress } from '../types/address'
import { AccountTransaction } from '../types/accountTransaction'

export const useUser = <U = User>(
  id = 'me'
): {
  user: U
  isLoggedIn: boolean
  fetching: boolean
  mutate: KeyedMutator<U>
} => {
  const { localiseUrl } = useLocale()
  const { data, mutate, isLoading } = useSWR(`/api/users/${id}`, (url) =>
    axios.get(localiseUrl(url, { localeAsParam: true })).then((res) => res.data)
  )

  return {
    user: data,
    isLoggedIn: !!data,
    fetching: isLoading,
    mutate
  }
}

export const useUserBillingAddress = (id = 'me') => {
  const { localiseUrl } = useLocale()
  const { user } = useUser(id)
  const { data, error, mutate } = useSWR(
    user &&
      `/api/users/${user?.id}/addresses/${user?.billing_address_id || ''}`,
    (url: string) =>
      axios
        .get(localiseUrl(url, { localeAsParam: true }))
        .then((res) => res.data)
  )

  return {
    address: Array.isArray(data) ? null : (data as BillingAddress),
    fetching: data === undefined && !error,
    mutate
  }
}

export const useUserStripeAccount = () => {
  const { localiseUrl } = useLocale()
  const { data, error, mutate } = useSWR(
    '/api/users/me/stripe_account',
    (url) =>
      axios
        .get(localiseUrl(url, { localeAsParam: true }))
        .then((res) => res.data)
  )

  return {
    account: data,
    fetching: data === undefined && !error,
    mutate
  }
}

export const useUserAccountTransactionsInfinite = (userId: number) => {
  // A function to get the SWR key of each page,
  // its return value will be accepted by `fetcher`.
  // If `null` is returned, the request of that page won't start.
  const getKey = (
    pageIndex: number,
    previousPageData:
      | APIResponseUser['/api/users/:id/account_transactions']
      | null
  ) => {
    if (!userId) return null
    if (previousPageData && !previousPageData.transactions?.length) return null // reached the end
    return `/api/users/${userId}/account_transactions?page=${pageIndex + 1}` // SWR key
  }
  const { localiseUrl } = useLocale()

  const {
    data: originalData,
    isValidating,
    error,
    size,
    setSize
  } = useSWRInfinite<APIResponseUser['/api/users/:id/account_transactions']>(
    (pageIndex, previousPageData) => getKey(pageIndex, previousPageData),
    (url) =>
      axios
        .get(localiseUrl(url, { localeAsParam: true }))
        .then((res) => res.data)
  )

  const transactionsForAllPages = useMemo(() => {
    const flattened: AccountTransaction[] | undefined = originalData
      ? []
      : originalData

    originalData?.forEach((transactionPage) => {
      const transactions = transactionPage.transactions

      transactions?.forEach((transaction) => {
        flattened?.push(transaction)
      })
    })
    return flattened
  }, [originalData])

  const numberOfPagesFetched = size

  const mostRecentPage = originalData?.[size - 1]
  const defaultCurrency = mostRecentPage?.default_currency
  const balancePerCurrency = mostRecentPage?.balance_per_currency || {}
  const totalPages = originalData?.[0]?.meta?.total_pages

  return {
    transactions: transactionsForAllPages,
    isValidating,
    error,
    size,
    setSize,
    noMoreData: totalPages ? totalPages === numberOfPagesFetched : true,
    defaultCurrency,
    balancePerCurrency
  }
}
